import React, { useState, useEffect } from 'react';
import {
  Show,
  useRecordContext,
  useRedirect,
  useDataProvider,
  useNotify,
} from 'react-admin';
import ResourceBreadcrumbsWithActions from '../../components/ResourceBreadcrumbsWithActions';
import { FranchisesTable } from '../../components/FranchisesTable';
import { FranchiseDetailDialog } from '../../components/FranchiseDetailDialog';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Container,
  Paper,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { RegionMetrics, FranchiseTenant } from '../../types/franchise';
import { formatCurrency, formatPercentage, getComplianceStatus } from '../../types/franchise';

const RegionSummaryCard = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: '#9c27b0',
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              <LocationIcon sx={{ fontSize: 40, color: 'white' }} />
            </Avatar>
          </Grid>

          <Grid item xs>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 1 }}>
              {record.name}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {record.brand?.name && (
                <Chip label={record.brand.name} size="small" color="primary" />
              )}
              {record.brand?.enterprise?.name && (
                <Chip label={record.brand.enterprise.name} size="small" variant="outlined" />
              )}
            </Box>

            {record.description && (
              <Typography variant="body1" color="text.secondary">
                {record.description}
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const MetricsCard = ({ metrics, error }: { metrics: RegionMetrics | null; error?: string }) => {
  if (!metrics) {
    return (
      <Card>
        <CardContent>
          <Typography color={error ? "error" : "textSecondary"}>
            {error || "Loading metrics..."}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const complianceStatus = getComplianceStatus(metrics.summary.averageComplianceScore);

  return (
    <Grid container spacing={2}>
      {/* Summary Metrics */}
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <PeopleIcon color="success" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {metrics.summary.totalFranchisees}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Franchisees
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <AssessmentIcon sx={{ color: complianceStatus.color }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {metrics.summary.averageComplianceScore !== null
              ? `${metrics.summary.averageComplianceScore.toFixed(1)}%`
              : 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Avg. Compliance
          </Typography>
        </Paper>
      </Grid>

      {/* Revenue Metrics */}
      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, bgcolor: 'success.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <MoneyIcon sx={{ color: 'success.main', mr: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Total Revenue
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
            {formatCurrency(metrics.revenue.totalRevenue)}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper elevation={1} sx={{ p: 2, bgcolor: 'primary.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <MoneyIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Total Royalties
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {formatCurrency(metrics.revenue.totalRoyalties)}
          </Typography>
        </Paper>
      </Grid>

      {/* Invoice Metrics */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              Invoice Performance
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Invoices
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {metrics.invoices.total}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Paid Invoices
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {metrics.invoices.paid}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice Revenue
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatCurrency(metrics.invoices.totalRevenue)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const FranchiseesTab = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [franchisees, setFranchisees] = useState<FranchiseTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFranchisee, setSelectedFranchisee] = useState<any | null>(null);

  useEffect(() => {
    if (!record?.id) return;

    const fetchFranchisees = async () => {
      try {
        const response = await dataProvider.getList('tenants', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { regionId: record.id },
        });
        setFranchisees(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load franchises');
      } finally {
        setLoading(false);
      }
    };

    fetchFranchisees();
  }, [record?.id, dataProvider]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <FranchisesTable
        franchisees={franchisees}
        loading={loading}
        error={error}
        showBrand={false}
        showRegion={false}
        onRowClick={(franchisee) => setSelectedFranchisee(franchisee)}
      />

      {/* Franchise Detail Dialog */}
      <FranchiseDetailDialog
        open={!!selectedFranchisee}
        franchisee={selectedFranchisee}
        onClose={() => setSelectedFranchisee(null)}
      />
    </Box>
  );
};

const RegionShowContent = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState(0);
  const [metrics, setMetrics] = useState<RegionMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  useEffect(() => {
    if (!record?.id) return;

    const fetchMetrics = async () => {
      setMetricsError(null);
      try {
        const response = await dataProvider.getOne('franchise-analytics/region', {
          id: record.id,
        });
        setMetrics(response.data as RegionMetrics);
      } catch (error) {
        console.error('Error fetching metrics:', error);
        setMetricsError('Metrics not available');
        notify('Failed to load region metrics', { type: 'warning' });
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [record?.id, dataProvider, notify]);

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box>
        <RegionSummaryCard />

        <Card sx={{ mt: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2 }}
            >
              <Tab label="Overview" />
              <Tab label="Franchises" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {activeTab === 0 && (
              <Box>
                {metricsLoading ? (
                  <Typography>Loading metrics...</Typography>
                ) : (
                  <MetricsCard metrics={metrics} error={metricsError} />
                )}
              </Box>
            )}
            {activeTab === 1 && <FranchiseesTab />}
          </Box>
        </Card>
      </Box>
    </Container>
  );
};

const RegionTitle = () => {
  const record = useRecordContext();
  if (!record) return 'Loading...';
  return record.name || 'Region';
};

export const RegionShow = () => (
  <Show actions={false} title={<RegionTitle />}>
    <ResourceBreadcrumbsWithActions hideEdit={false} hideDelete={false} />
    <RegionShowContent />
  </Show>
);
